import * as React from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FileUpload from '@cloudscape-design/components/file-upload';
import FormField from '@cloudscape-design/components/form-field';
import { useState } from 'react';
import Spinner from '@cloudscape-design/components/spinner';
import mergeicon from '../assets/mergeicon.png';
import { addUsage } from './add-usage.js';
import { PDFDocument } from 'pdf-lib';
import constants from '../constants.js';

const MergePDF = () => {
  const [visible, setVisible] = React.useState(false);
  const [value, setValue] = React.useState([]);
  const [submitvalue, setSubmitvalue] = useState(['Merge']);
  const [servicevalue, setServicevalue] = useState([]);
  const [filearray, setfilearray] = useState([]);
  const [totalsize, setTotalsize] = useState([]);
  const [message, setMessage] = useState([]);
  const [notify, setNotify] = useState(null);
  const [error, setError] = useState(null);
  const [organizedFiles, setOrganizedFiles] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [filePreviews, setFilePreviews] = useState([]);

  const toggleModal = React.useCallback(
    (service) => () => {
      setVisible(true);
      setValue('');
      setServicevalue(service);
      setSubmitvalue('Merge');
      setTotalsize(0);
      setNotify('');
      setError('');
    },
    [],
  );

  const onClickFooterButton = () => {
    setVisible(false);
    setValue('');
    setSubmitvalue('Merge');
    setTotalsize(0);
    setNotify('');
    setError('');
    setFilePreviews([]);
  };

  async function _handleInputChange(e) {
    setValue(e.detail.value);
    let files = e.detail.value;
    const pdfFiles = files.filter((file) => file.type === constants.pdfFileType);

    if (pdfFiles.length !== files.length) {
      setNotify(constants.onlyPdfAllowed);
      setError('error');
    } else {
      setNotify('');
      setError(null);
    }

    let valueAdded = 0;
    for (const file of pdfFiles) {
      valueAdded += file.size;
    }
    setTotalsize(valueAdded / 1000000);
    if (totalsize > 2000) {
      setSubmitvalue('Limit Exceeded');
    } else if (totalsize <= 0) {
      setSubmitvalue('Merge');
    }
    setfilearray(pdfFiles);
    setOrganizedFiles(pdfFiles);
    setSubmitvalue('Merge');
    setMessage('*Select more than one file.');
    await generatePreviews(pdfFiles);
  }

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    const newFiles = [...organizedFiles];
    const newPreviews = [...filePreviews];
    const draggedFile = newFiles[draggedIndex];
    const draggedPreview = newPreviews[draggedIndex];

    newFiles.splice(draggedIndex, 1);
    newFiles.splice(dropIndex, 0, draggedFile);
    newPreviews.splice(draggedIndex, 1);
    newPreviews.splice(dropIndex, 0, draggedPreview);

    setOrganizedFiles(newFiles);
    setFilePreviews(newPreviews);
    setDraggedIndex(null);
  };

  const removeFile = (indexToRemove) => {
    const newFiles = organizedFiles.filter((_, index) => index !== indexToRemove);
    const newPreviews = filePreviews.filter((_, index) => index !== indexToRemove);

    setOrganizedFiles(newFiles);
    setFilePreviews(newPreviews);
    setfilearray(newFiles);

    let valueAdded = 0;
    for (const file of newFiles) {
      valueAdded += file.size;
    }
    setTotalsize(valueAdded / 1000000);
  };

  const rotateFile = (index) => {
    const newPreviews = [...filePreviews];
    const currentRotation = newPreviews[index].rotation || 0;
    newPreviews[index].rotation = (currentRotation + 90) % 360;
    setFilePreviews(newPreviews);
  };

  const generatePreviews = async (files) => {
    const previews = files.map((file) => {
      if (file.type === constants.pdfFileType) {
        return {
          file,
          thumbnail: URL.createObjectURL(file),
          pageCount: 0,
          rotation: 0,
        };
      }
      return { file, thumbnail: null, pageCount: 0, rotation: 0 };
    });
    setFilePreviews(previews);
  };

  async function Merging() {
    setSubmitvalue(<Spinner />);

    try {
      const mergedPdfDoc = await PDFDocument.create();

      for (let i = 0; i < organizedFiles.length; i++) {
        const file = organizedFiles[i];
        if (file.type === constants.pdfFileType) {
          try {
            const arrayBuffer = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            const copiedPages = await mergedPdfDoc.copyPages(pdfDoc, pdfDoc.getPageIndices());
            const preview = filePreviews.find((p) => p.file === file);
            const rotation = preview?.rotation || 0;

            copiedPages.forEach((page) => {
              if (rotation !== 0) {
                page.setRotation({ angle: rotation, type: 'degrees' });
                if (rotation === 90 || rotation === 270) {
                  const { width, height } = page.getSize();
                  const scale = Math.min(width / height, height / width);
                  page.scale(scale, scale);
                }
              }
              mergedPdfDoc.addPage(page);
            });
          } catch (fileError) {
            console.error(`Error processing ${file.name}:`, fileError);
            try {
              const { handleEncryptedPDF } = await import('./pdf-encryption-handler.js');
              const processedPdfBytes = await handleEncryptedPDF(file);
              const processedPdfDoc = await PDFDocument.load(processedPdfBytes);
              const copiedPages = await mergedPdfDoc.copyPages(processedPdfDoc, processedPdfDoc.getPageIndices());
              copiedPages.forEach((page) => mergedPdfDoc.addPage(page));
            } catch (encryptionError) {
              console.error(`Failed to process encrypted PDF ${file.name}:`, encryptionError);
              setNotify(`"${file.name}" could not be processed. Please check if it's a valid PDF.`);
              setError('error');
              setSubmitvalue('Merge');
              return;
            }
          }
        }
      }

      const mergedPdfBytes = await mergedPdfDoc.save();
      const blob = new Blob([mergedPdfBytes], { type: constants.pdfFileType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'awesomepdf_merged.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setSubmitvalue('Success');
      addUsage('AWESOME PDF TOOLS', '1.0', filearray.length, window.location.href);
      setVisible(false);
      setFilePreviews([]);
    } catch (error) {
      console.error('Merge error:', error);
      setNotify('Merge failed. Please check if all files are valid PDFs.');
      setError('error');
      setSubmitvalue('Merge');
    }
  }

  return (
    <div>
      <div className="service-card" onClick={toggleModal('Merge PDFs')}>
        <span className="badge badge-popular">POPULAR</span>
        <div className="service-card-icon">
          <img src={mergeicon} alt="Merge PDF" loading="lazy" />
        </div>
        <h3 className="service-card-title">Merge PDF</h3>
        <p className="service-card-desc">Combine multiple PDFs into one single document online</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={() => {
          setVisible(false);
          setFilePreviews([]);
        }}
        visible={visible}
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onClickFooterButton}>
                Cancel
              </Button>
              <div>
                {totalsize < 2000 && totalsize > 0 && filearray.length > 1 && !error ? (
                  <Button variant="primary" onClick={Merging}>
                    {submitvalue}
                  </Button>
                ) : (
                  <div>
                    {totalsize > 2000 ? (
                      <Button disabled variant="primary">
                        Limit Exceeded
                      </Button>
                    ) : (
                      <Button disabled variant="primary">
                        {submitvalue}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </SpaceBetween>
          </Box>
        }
        header={servicevalue}
      >
        {servicevalue === 'Merge PDFs' ? (
          <div>
            <FormField errorText={notify} label="Upload file" description="Only PDF format is supported">
              <FileUpload
                onChange={_handleInputChange}
                value={[]}
                i18nStrings={{
                  uploadButtonText: (e) => (e ? 'Choose files' : 'Choose'),
                  dropzoneText: (e) => (e ? 'Drop files to upload' : 'Drop file to upload'),
                  removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                  limitShowFewer: 'Show fewer files',
                  limitShowMore: 'Show more files',
                  errorIconAriaLabel: 'Error',
                }}
                multiple
                showFileLastModified
                showFileSize
                showFileThumbnail
                tokenLimit={9}
                constraintText=""
                accept=".pdf"
              />
            </FormField>

            {filePreviews.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '10px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                  }}
                >
                  {filePreviews.map((preview, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                      style={{
                        padding: '8px',
                        margin: '4px 0',
                        backgroundColor: draggedIndex === index ? '#FFF8E7' : '#fafafa',
                        border: `1px solid ${draggedIndex === index ? '#FF9900' : '#e0e0e0'}`,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        cursor: 'move',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <span style={{ fontWeight: 'bold', minWidth: '20px', color: '#232F3E' }}>{index + 1}.</span>

                      <div
                        style={{
                          width: '80px',
                          height: '100px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#fff',
                          fontSize: '10px',
                          textAlign: 'center',
                          color: '#666',
                        }}
                      >
                        {preview.thumbnail ? (
                          <iframe
                            src={preview.thumbnail}
                            width="78"
                            height={preview.rotation === 90 || preview.rotation === 270 ? '80' : '98'}
                            style={{
                              border: 'none',
                              borderRadius: '3px',
                              transform: `rotate(${preview.rotation || 0}deg)`,
                            }}
                          />
                        ) : (
                          <div>
                            PDF
                            <br />
                            File
                          </div>
                        )}
                      </div>

                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', marginBottom: '4px' }}>{preview.file.name}</div>
                        <div style={{ color: '#666', fontSize: '0.85em' }}>
                          {(preview.file.size / 1024 / 1024).toFixed(2)} MB
                          {preview.pageCount > 0 && ` • ${preview.pageCount} pages`}
                        </div>
                      </div>

                      <SpaceBetween direction="horizontal" size="xs">
                        <Button
                          variant="icon"
                          iconName="refresh"
                          onClick={() => rotateFile(index)}
                          ariaLabel="Rotate file"
                        />
                        <Button
                          variant="icon"
                          iconName="close"
                          onClick={() => removeFile(index)}
                          ariaLabel="Remove file"
                        />
                      </SpaceBetween>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div></div>
        )}
        <br />
        Current size: {totalsize}
        <span>Mb</span>
        <br />
        Total maximum limit: 2GB
        <div>{filearray.length < 2 ? <p style={{ color: '#CC0C39' }}>{message}</p> : null}</div>
      </Modal>
    </div>
  );
};

export default MergePDF;
